import React, { useEffect, useRef, useState } from "react";
import AddEditModal from "@services/ui-components/src/AddEditModal/AddEditModal";
import { ErrorMessage, Form, Formik } from "formik";
import {
    getCategoryName,
    getStartValueCategory,
    getStartValueSubCategory,
    getSubCategoryName,
    techMapFormValidationSchema,
} from "../constants";
import { Button, Col, Container, Row } from "react-bootstrap";
import TextInputWithCounter from "../inputs/TextInputWithCounter";
import { CustomModal, Spinner, SpinnerLoader, TextareaInputWithCounter } from "@services/ui-components";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import i18next from "i18next";
import CategoryTechMaps from "../services/CategoryTechMaps";
import RequestInputService from "../services/RequestInputService";
import { normalizeCategoryData } from "../services/normalizeCategoryData";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";

import { ActionUpdateType } from "../../../utils/enums";
import { CategoryInterface, DuplicateTechMapData } from "../types";
import {
    useCreateTechMapsCategoryMutation,
    useGetTechMapsCategoriesQuery,
    useRestoreTechnologicalMapMutation,
    useUpdateTechnologicalMapMutation,
} from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import {
    TechMapCategoryNewInput,
    TechMapCategoryNewProxy,
    TechnologicalMapInput,
    TechnologicalMapProxy,
} from "@me-team/host/main/graphql/types";
import { useNavigate, useParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import UpdateRecoveryTechMapEffects from "./UpdateRecoveryTechMapEffects";

interface ValidationError {
    [key: string]: { message: string; code: string | null }[];
}

interface UpdateRecoveryTechMapProps {
    show: boolean;
    onClose: () => void;
    currentAction: string;
    currentUpdateTechMap: TechnologicalMapProxy;
    refetchTechMaps: () => void;
    toggleShowToast: (text: string) => void;
    canEditTechMap?: boolean;
    canDeleteRestoreTechMap?: boolean;
    canCreateTechMapCategory?: boolean;
}

const UpdateRecoveryTechMap: React.FC<UpdateRecoveryTechMapProps> = ({
    show,
    onClose,
    currentAction,
    currentUpdateTechMap,
    refetchTechMaps,
    toggleShowToast,
    canEditTechMap,
    canDeleteRestoreTechMap,
    canCreateTechMapCategory,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const navigate = useNavigate();
    const isEditModal = currentAction === ActionUpdateType.Edit;
    const canSubmit = isEditModal ? (canEditTechMap ?? true) : (canDeleteRestoreTechMap ?? true);
    const canSubmitTechMapCategory = canCreateTechMapCategory ?? true;
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const [isTechMapModalOpen, setIsTechMapModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [isDuplicateTechMap, setIsDuplicateTechMap] = useState<boolean>(false);
    const [pickedCategoryId, setPickedCategoryId] = useState<number>(null);
    const [duplicateTechMapData, setDuplicateTechMapData] = useState<DuplicateTechMapData | null>(null);

    const startValueCategory = getStartValueCategory(currentUpdateTechMap?.technologicalMapNewCategory);
    const startValueSubCategory = getStartValueSubCategory(currentUpdateTechMap?.technologicalMapNewCategory);
    const categoryName = getCategoryName(currentUpdateTechMap?.technologicalMapNewCategory);
    const subCategoryName = getSubCategoryName(currentUpdateTechMap?.technologicalMapNewCategory);

    const [categoryText, setCategoryText] = useState([categoryName, subCategoryName]);
    const selectedCategory = categoryText[0] || "";
    const selectedSubcategory = categoryText[1] || "";

    useEffect(() => {
        if (categoryName || subCategoryName) {
            setCategoryText([categoryName ?? "", subCategoryName ?? ""]);
        }
    }, [categoryName, subCategoryName]);

    const [createTechMapCategory] = useCreateTechMapsCategoryMutation();
    const [restoreTechnologicalMap] = useRestoreTechnologicalMapMutation();
    const [updateTechnologicalMap] = useUpdateTechnologicalMapMutation();

    const {
        data: techMapCategoriesData,
        refetch: refetchCategories,
        loading: techMapCategoryLoading,
    } = useGetTechMapsCategoriesQuery({
        variables: {
            itemsPerPage: 1000,
            delete: false,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(
                CategoryTechMaps.categoryModuleCreator(normalizedItems, startValueCategory, startValueSubCategory)
            );
            setCategoryInput(
                RequestInputService.createServiceCategoryInput(
                    startValueCategory,
                    startValueSubCategory,
                    normalizedItems
                )
            );
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const categoryListData: TechMapCategoryNewProxy[] = techMapCategoriesData?.TechMapCategories?.items;

    const handleSubmitCreateCategory = async (input: TechMapCategoryNewInput) => {
        if (!canSubmitTechMapCategory) return;

        try {
            currentCompanyId &&
                (await createTechMapCategory({
                    variables: {
                        companyId: +currentCompanyId,
                        input: input,
                        branchId: Number(branchId),
                    },
                    onCompleted: (data) => {
                        data && setCurrentCategory(data.createTechMapCategory);
                    },
                    onError: (error) => {
                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                    },
                }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenDuplicateOfTechMapModal = () => {
        setIsDuplicateTechMap(true);
    };
    const handleCloseDuplicateOfTechMapModal = () => {
        setIsDuplicateTechMap(false);
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const handleCategoryNameChange = (categoryName: string) => {
        setCategoryText([categoryName]);
    };

    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string) => {
        setCategoryText([categoryName, subCategoryName]);
    };

    const resetInitialStateCategory = () => {
        setCategoryText([categoryName, subCategoryName]);
        setPickedCategoryId(null);
    };

    const initialValues: TechnologicalMapInput = {
        name: currentUpdateTechMap?.name && currentUpdateTechMap?.name,
        categoryId:
            currentUpdateTechMap?.technologicalMapNewCategory?.id &&
            currentUpdateTechMap?.technologicalMapNewCategory?.id,
        description: currentUpdateTechMap?.description ? currentUpdateTechMap?.description : "",
    };

    const handleUpdateSubmit = async (values: TechnologicalMapInput) => {
        if (!canSubmit) return;

        const input: TechnologicalMapInput = {
            name: values?.name,
            categoryId: values?.categoryId,
            description: values?.description ? values?.description : null,
        };

        const mutateTechnologicalMap = isEditModal ? updateTechnologicalMap : restoreTechnologicalMap;

        await mutateTechnologicalMap({
            variables: { technologicalMapId: +currentUpdateTechMap?.id, input, branchId: Number(branchId) },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                refetchTechMaps();
                resetInitialStateCategory();
                data && onClose();
                toggleShowToast(isEditModal ? t("Saved") : t("Restored"));
            },
            onError: (error: ApolloError) => {
                const validationErrors = (error?.graphQLErrors[0]?.extensions as { validation?: ValidationError })
                    ?.validation;

                if (validationErrors && validationErrors[""]) {
                    validationErrors[""]?.forEach((err: any) => {
                        const message: string = err.message;

                        if (message.includes("The technological map already exists with")) {
                            const regex = /name\s*=\s*"(.+?)"\s*and\s*id\s*=\s*"(.+?)"/;
                            const match = message.match(regex);

                            if (match) {
                                const [, name, id] = match;
                                const extractedData: DuplicateTechMapData = {
                                    name,
                                    techMapId: id,
                                };
                                setDuplicateTechMapData(extractedData);
                                handleOpenDuplicateOfTechMapModal();
                            }
                        }
                    });
                } else {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                }
            },
        });
    };

    const handleCloseCreateTechMapModal = () => {
        onClose();
    };

    const handleNavigateAndClose = () => {
        handleCloseDuplicateOfTechMapModal();
        onClose();
        navigate(
            `/warehouse/technological-maps/${currentCompanyId}/${duplicateTechMapData?.techMapId}/products/branch/${branchId}`
        );
    };

    if (techMapCategoryLoading) return <Spinner />;
    return (
        <>
            <AddEditModal
                isModalOpened={show}
                setIsModalOpened={handleCloseCreateTechMapModal}
                title={isEditModal ? t("Edit Technological Map") : t("Restore Technological Map")}
                textAlign="start"
                contentClassName={`${isDuplicateTechMap ? "d-none " : "d-block"}`}
            >
                <>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={techMapFormValidationSchema(t)}
                        onSubmit={handleUpdateSubmit}
                    >
                        {({ isSubmitting, handleReset, touched, errors }) => {
                            return (
                                <Form>
                                    <UpdateRecoveryTechMapEffects pickedCategoryId={pickedCategoryId} />
                                    <Row className="m-0 mb-3 mb-sm-4">
                                        <TextInputWithCounter
                                            id="createTechMap-name-field"
                                            name="name"
                                            label="Technological Map Name"
                                            placeholder="ModalTechMap.Enter a Name"
                                            isDisabled={!canSubmit}
                                        />
                                    </Row>
                                    <Row className="m-0 mb-2 mb-sm-4">
                                        <Col sm={3} className="mb-2 mb-md-0">
                                            <label
                                                htmlFor="createTechMap-categoryId-field"
                                                className="text-dark fs-7 fw-bold py-1"
                                                onClick={handleLabelClick}
                                            >
                                                {t("Category")}&nbsp;<span className="text-danger">*</span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <div>
                                                <span
                                                    className={`${selectedCategory ? "me-4" : "d-none"}  mb-2 mb-lg-0 text-dark`}
                                                >
                                                    {selectedCategory}{" "}
                                                    {selectedSubcategory ? (
                                                        <>
                                                            <i className="bi bi-arrow-right"></i> {selectedSubcategory}
                                                        </>
                                                    ) : null}
                                                </span>
                                                <Button
                                                    ref={buttonCategoryRef}
                                                    variant={`${touched.categoryId && errors.categoryId ? "outline-danger" : "outline-primary"}`}
                                                    className="w-100 fw-normal py-1"
                                                    id="createTechMap-categoryId-field"
                                                    style={{ maxWidth: "149px" }}
                                                    disabled={!canSubmit}
                                                    onClick={() => {
                                                        if (!canSubmit) return;
                                                        setIsTechMapModalOpen(true);
                                                    }}
                                                >
                                                    {selectedCategory ? t("Change Category") : t("Select Category")}
                                                </Button>
                                                <ErrorMessage name="categoryId">
                                                    {(msg) => (
                                                        <div className="text-danger d-block fs-7 mt-1">{msg}</div>
                                                    )}
                                                </ErrorMessage>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="m-0">
                                        <TextareaInputWithCounter
                                            id="createTechMap-name-field"
                                            name="description"
                                            label={t("Description")}
                                            labelCol={3}
                                            inputCol={9}
                                            maxLength={400}
                                            placeholder={t("Enter a Description")}
                                        />
                                    </Row>
                                    <div className="d-flex justify-content-end m-0 mt-4 gx-2">
                                        <Button
                                            onClick={() => {
                                                handleReset();
                                                onClose();
                                            }}
                                            variant="outline-primary"
                                            type="button"
                                            disabled={isSubmitting}
                                            className="custom-btn fw-normal me-2"
                                        >
                                            {t("Cancel")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="custom-btn"
                                            disabled={isSubmitting || !canSubmit}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader
                                                    variant="light"
                                                    loadingText={isEditModal ? t("Editing...") : t("Restoring...")}
                                                />
                                            ) : isEditModal ? (
                                                t("Save")
                                            ) : (
                                                t("Restore")
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>

                    {isTechMapModalOpen && techMapCategoriesData && !techMapCategoryLoading ? (
                        <ServiceModalWrapper
                            companyId={Number(currentCompanyId)}
                            branchId={Number(branchId)}
                            i18n={i18next}
                            selectedCategoryId={null}
                            selectedSubcategoryId={null}
                            apolloUrl={process.env.REACT_APP_API_URL}
                            isOpen={isTechMapModalOpen}
                            onClose={() => {
                                setIsTechMapModalOpen(!isTechMapModalOpen);
                            }}
                            onCategorySelect={(selectedCategory) => {
                                handleCategoryNameChange(selectedCategory?.name);
                                setPickedCategoryId(selectedCategory?.id);
                                setIsTechMapModalOpen(!isTechMapModalOpen);
                            }}
                            onSubcategorySelect={(selectedSubcategory) => {
                                handleSubCategoryNameChange(
                                    selectedSubcategory?.name,
                                    selectedSubcategory?.parentCategory?.name
                                );
                                setPickedCategoryId(selectedSubcategory?.id);
                                setIsTechMapModalOpen(!isTechMapModalOpen);
                            }}
                            categoryList={categoryList}
                            categoryInput={categoryInput}
                            categoryListData={categoryListData}
                            refetchCategories={refetchCategories}
                            onSubmitCreate={handleSubmitCreateCategory}
                            currentCategory={currentCategory}
                            categoryService={CategoryTechMaps}
                            requestInputService={RequestInputService}
                            normalizeCategoryData={normalizeCategoryData}
                        />
                    ) : null}

                    {isErrorModalOpen ? (
                        <ErrorModalComponent
                            i18n={i18next}
                            onClose={() => {
                                setIsErrorModalOpen(null);
                            }}
                            isOpen={!!isErrorModalOpen}
                            currentError={isErrorModalOpen}
                        />
                    ) : null}
                </>
            </AddEditModal>
            <CustomModal
                show={isDuplicateTechMap}
                handleClose={handleCloseDuplicateOfTechMapModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("The technological map already exists")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-16 me-1">
                                    {t(
                                        "Such a Technological Map already exists. You can view its products via the link."
                                    )}
                                </span>
                                <div>
                                    <Button
                                        variant="link"
                                        className="fs-normal text-primary text-decoration-underline cursor-pointer"
                                        onClick={handleNavigateAndClose}
                                    >
                                        {`${duplicateTechMapData?.name}`}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("ProductDuplicate.Back"),
                        onClick: handleCloseDuplicateOfTechMapModal,
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                ]}
            />
        </>
    );
};

export default UpdateRecoveryTechMap;
